import { createAction, handleActions } from 'redux-actions';
import { normalizeCreditCard } from '@lib/braintree/braintree';
import { PaymentMethodData } from '@api/types';
import { Trace, TraceStatus, RootState } from '@redux/types';

export type CardFields = 'number' | 'expirationDate' | 'cvv';

export interface CardFieldValidation {
  isEmpty: boolean;
  isValid: boolean;
  isPotentiallyValid: boolean;
}

const emptyValidation: CardFieldValidation = {
  isEmpty: true,
  isValid: false,
  isPotentiallyValid: true,
};

export type CardValidationFields = { [key in CardFields]: CardFieldValidation };

export interface PaymentMethod {
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  trace: Trace;
  isAlertOpen: boolean;
  supportedCardTypes: string[];
  paymentMethod: Nullable<PaymentMethodData>;
  validationFields: CardValidationFields;
  cardType: string;
  isLoadingPaymentMethod: boolean;
}

export const initialState: PaymentMethod = {
  isLoading: false,
  isError: false,
  isSuccess: false,
  trace: {
    status: TraceStatus.Info,
    message: '',
  },
  isAlertOpen: false,
  supportedCardTypes: [],
  paymentMethod: null,
  validationFields: {
    number: emptyValidation,
    expirationDate: emptyValidation,
    cvv: emptyValidation,
  },
  cardType: '',
  isLoadingPaymentMethod: false,
};

// Actions

export const FETCH_PAYMENT_METHOD_STARTED = 'payment-methods/FETCH_PAYMENT_METHOD_STARTED';
export const FETCH_PAYMENT_METHOD_SUCCEED = 'payment-methods/FETCH_PAYMENT_METHOD_SUCCEED';
export const FETCH_PAYMENT_METHOD_FAILED = 'payment-methods/FETCH_PAYMENT_METHOD_FAILED';
export const INSTANCE_CREATION_STARTED = 'payment-methods/INSTANCE_CREATION_STARTED';
export const INSTANCE_CREATION_SUCCEEDED = 'payment-methods/INSTANCE_CREATION_SUCCEEDED';
export const CARD_UPDATE_STARTED = 'payment-methods/CARD_UPDATE_STARTED';
export const CARD_UPDATE_SUCCEEDED = 'payment-methods/CARD_UPDATE_SUCCEEDED';
export const CARD_UPDATE_FAILED = 'payment-methods/CARD_UPDATE_FAILED';
export const CARD_UPDATE_CANCELLED = 'payment-methods/CARD_UPDATE_CANCELLED';
export const CARD_VALIDITY_CHANGED = 'payment-methods/CARD_VALIDITY_CHANGED';
export const CARD_TYPE_CHANGED = 'payment-methods/CARD_TYPE_CHANGED';
export const ALERT_CLOSE = 'payment-methods/ALERT_CLOSE';

// Action creators

export const fetchPaymentMethod = createAction(FETCH_PAYMENT_METHOD_STARTED);
export const fetchPaymentMethodSucceed = createAction(FETCH_PAYMENT_METHOD_SUCCEED);
export const fetchPaymentMethodFailed = createAction(FETCH_PAYMENT_METHOD_FAILED);
export const createInstance = createAction(INSTANCE_CREATION_STARTED);
export const createInstanceSuccess = createAction(INSTANCE_CREATION_SUCCEEDED);
export const updateCard = createAction(CARD_UPDATE_STARTED);
export const updateCardSuccess = createAction(CARD_UPDATE_SUCCEEDED);
export const updateCardFailure = createAction(CARD_UPDATE_FAILED);
export const updateCardCancel = createAction(CARD_UPDATE_CANCELLED);
export const changeCardValidity = createAction(CARD_VALIDITY_CHANGED);
export const changeCardType = createAction(CARD_TYPE_CHANGED);
export const closeAlert = createAction(ALERT_CLOSE);

// Reducer

const handlePaymentMethodFetchStarted = (state: PaymentMethod): PaymentMethod => ({
  ...state,
  isLoadingPaymentMethod: true,
});

const handlePaymentMethodFetchSucceed = (state: PaymentMethod, { payload }): PaymentMethod => ({
  ...state,
  paymentMethod: payload,
  isLoadingPaymentMethod: false,
});

const handlePaymentMethodFetchFailed = (state: PaymentMethod): PaymentMethod => ({
  ...state,
  isLoadingPaymentMethod: false,
});

const handleInstanceCreationStarted = (state: PaymentMethod): PaymentMethod => ({
  ...state,
  isLoading: true,
  isAlertOpen: false,
});

const handleInstanceCreationSucceeded = (state: PaymentMethod, { payload }): PaymentMethod => ({
  ...state,
  supportedCardTypes: payload,
  isLoading: false,
});

const handleCardUpdateStarted = (state: PaymentMethod): PaymentMethod => ({
  ...state,
  isLoading: true,
});

const handleCardUpdateSucceeded = (state: PaymentMethod, { payload }): PaymentMethod => ({
  ...state,
  paymentMethod: payload,
  trace: {
    status: TraceStatus.Info,
    message: t('message.changes-saved-successfully'),
  },
  isAlertOpen: true,
  isLoading: false,
  isError: false,
  isSuccess: true,
});

const handleCardUpdateFailed = (state: PaymentMethod): PaymentMethod => ({
  ...state,
  isLoading: false,
  isError: true,
  isSuccess: false,
});

const handleCardUpdateCancelled = (state: PaymentMethod): PaymentMethod => {
  const { paymentMethod } = state;
  return {
    ...state,
    ...initialState,
    paymentMethod,
  };
};

const handleCardValidityChanged = (state: PaymentMethod, { payload }): PaymentMethod => ({
  ...state,
  validationFields: payload,
});

const handleCardTypeChanged = (state: PaymentMethod, { payload }): PaymentMethod => {
  const normalizedCard = normalizeCreditCard(payload);
  const cardType = state.supportedCardTypes.includes(normalizedCard) ? normalizedCard : '';

  return {
    ...state,
    cardType,
  };
};

const handleAlertClose = (state: PaymentMethod): PaymentMethod => ({
  ...state,
  isAlertOpen: false,
});

export default handleActions(
  {
    [FETCH_PAYMENT_METHOD_STARTED]: handlePaymentMethodFetchStarted,
    [FETCH_PAYMENT_METHOD_SUCCEED]: handlePaymentMethodFetchSucceed,
    [FETCH_PAYMENT_METHOD_FAILED]: handlePaymentMethodFetchFailed,
    [INSTANCE_CREATION_STARTED]: handleInstanceCreationStarted,
    [INSTANCE_CREATION_SUCCEEDED]: handleInstanceCreationSucceeded,
    [CARD_UPDATE_STARTED]: handleCardUpdateStarted,
    [CARD_UPDATE_SUCCEEDED]: handleCardUpdateSucceeded,
    [CARD_UPDATE_FAILED]: handleCardUpdateFailed,
    [CARD_UPDATE_CANCELLED]: handleCardUpdateCancelled,
    [CARD_VALIDITY_CHANGED]: handleCardValidityChanged,
    [CARD_TYPE_CHANGED]: handleCardTypeChanged,
    [ALERT_CLOSE]: handleAlertClose,
  },
  initialState,
);

// Selectors

export const getPaymentMethodState = (state: RootState) => state.paymentMethods;

export const getPaymentMethod = (state: RootState) => ({
  isLoadingPaymentMethod: state.paymentMethods.isLoadingPaymentMethod,
  paymentMethod: state.paymentMethods.paymentMethod,
});

export const getTrace = (state: RootState) => ({
  trace: state.paymentMethods.trace,
  isAlertOpen: state.paymentMethods.isAlertOpen,
});

export const getSupportedCardTypes = (state: RootState) => state.paymentMethods.supportedCardTypes;

export const getIsLoading = (state: RootState) => state.paymentMethods.isLoading;

export const getCardType = (state: RootState) => state.paymentMethods.cardType;
